import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, ChevronDown } from "lucide-react";
import { siteConfig } from "@/config/siteConfig";
import { SOLUTIONS_NAV, PRODUCTS_NAV } from "@/lib/routes";
import { trackCtaClick } from "@/lib/analytics";

const SUPPORT_NAV = [
  { key: "technical_support", path: "/technical-support" },
  { key: "global_distribution", path: "/global-distribution" },
];

// Final 9-item nav — GLOBAL DISTRIBUTION removed (still reachable from footer
// and the homepage distribution section). Sized to fit at 1280 px.
const primaryNav = [
  { key: "home", to: "/" },
  { key: "solutions", to: "/solutions/blister-printing", dropdown: "solutions" },
  { key: "products", to: "/products/blister-pack-violet-ink", dropdown: "products" },
  { key: "industries", to: "/industries/pharmaceutical" },
  { key: "technical_support", to: "/technical-support" },
  { key: "become_distributor", to: "/distributors" },
  { key: "resources", to: "/resources" },
  { key: "about", to: "/about" },
  { key: "contact", to: "/contact" },
];

const dropdownConfig = {
  solutions: { list: SOLUTIONS_NAV, ns: "solutions" },
  products: { list: PRODUCTS_NAV, ns: "products" },
  support: { list: SUPPORT_NAV, ns: "nav" },
};

const Header = () => {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openMobileGroup, setOpenMobileGroup] = useState(null);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  return (
    <header
      className="sticky top-0 z-40 w-full bg-white border-b border-[var(--border)]"
      data-testid="site-header"
    >
      <div className="mx-auto max-w-[1360px] px-6 lg:px-6 h-[96px] flex items-center justify-between gap-4">
        <Link
          to="/"
          className="flex items-center whitespace-nowrap flex-shrink-0"
          aria-label={`${siteConfig.COMPANY_NAME} home`}
          data-testid="site-logo"
        >
          <img
            src={siteConfig.LOGO_PATH}
            alt={siteConfig.COMPANY_NAME}
            width="200"
            height="48"
            loading="eager"
            decoding="async"
            className="h-10 md:h-12 w-auto block"
            data-testid="site-logo-img"
          />
        </Link>

        {/* Desktop nav — visible from 1460 px. At 1440 the nav+CTAs cluster
            right-edge would land at 1443 px, so we push the threshold up by
            20 px to guarantee the outline CTA is fully inside the viewport. */}
        <nav className="hidden min-[1460px]:flex items-center space-x-3" aria-label="Primary">
          {primaryNav.map((item) => {
            if (item.dropdown) {
              const { list, ns } = dropdownConfig[item.dropdown];
              const isOpen = openDropdown === item.key;
              return (
                <div
                  key={item.key}
                  className="relative"
                  onMouseEnter={() => setOpenDropdown(item.key)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-[13.5px] uppercase tracking-[0.01em] text-[var(--charcoal)] hover:text-[var(--navy)] transition-colors whitespace-nowrap py-2"
                    aria-expanded={isOpen}
                    data-testid={`nav-${item.key}-trigger`}
                    onClick={() => setOpenDropdown(isOpen ? null : item.key)}
                  >
                    {t(`nav.${item.key}`)} <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                  {isOpen && (
                    <div
                      className="absolute left-0 top-full pt-2"
                      data-testid={`nav-${item.key}-dropdown`}
                    >
                      <div className="min-w-[260px] bg-white border border-[var(--border)] shadow-sm">
                        <ul className="py-2">
                          {list.map((li) => (
                            <li key={li.path}>
                              <Link
                                to={li.path}
                                className="block px-4 py-2 text-sm text-[var(--charcoal)] hover:bg-[var(--bg-alt)] hover:text-[var(--navy)]"
                                data-testid={`nav-${item.key}-item-${li.key}`}
                              >
                                {t(`${ns}.${li.key}`)}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            return (
              <NavLink
                key={item.key}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `text-[13.5px] uppercase tracking-[0.01em] transition-colors whitespace-nowrap py-2 ${
                    isActive ? "text-[var(--navy)]" : "text-[var(--charcoal)] hover:text-[var(--navy)]"
                  }`
                }
                data-testid={`nav-${item.key}`}
              >
                {t(`nav.${item.key}`)}
              </NavLink>
            );
          })}
        </nav>

        <div className="hidden min-[1460px]:flex items-center gap-2">
          <Link
            to="/request-sample"
            className="btn-primary text-[12px] px-4 py-2.5 whitespace-nowrap"
            data-testid="cta-request-sample-header"
            onClick={() => trackCtaClick("header", "request_sample")}
          >
            {t("nav.cta_sample_short")}
          </Link>
          <Link
            to="/contact"
            className="btn-outline text-[12px] px-4 py-2.5 whitespace-nowrap"
            data-testid="cta-talk-header"
            onClick={() => trackCtaClick("header", "talk_to_team")}
          >
            {t("nav.cta_talk_short")}
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          className="min-[1460px]:hidden p-2 -mr-2 text-[var(--charcoal)]"
          onClick={() => setMobileOpen(true)}
          aria-label={t("nav.open_menu")}
          data-testid="mobile-menu-open"
        >
          <Menu className="w-6 h-6" />
        </button>
      </div>

      {/* Mobile slide-in */}
      {mobileOpen && (
        <div className="min-[1460px]:hidden fixed inset-0 z-50" data-testid="mobile-menu-panel">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute right-0 top-0 h-full w-[86%] max-w-[380px] bg-white flex flex-col">
            <div className="h-[96px] px-6 flex items-center justify-between border-b border-[var(--border)]">
              <img
                src={siteConfig.LOGO_PATH}
                alt={siteConfig.COMPANY_NAME}
                width="180"
                height="40"
                className="h-10 w-auto block"
                data-testid="site-logo-mobile"
              />
              <button
                type="button"
                className="p-2 -mr-2"
                onClick={() => setMobileOpen(false)}
                aria-label={t("nav.close_menu")}
                data-testid="mobile-menu-close"
              >
                <X className="w-6 h-6 text-[var(--charcoal)]" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-6 py-6" aria-label="Mobile">
              <ul className="space-y-1">
                {primaryNav.map((item) => {
                  if (item.dropdown) {
                    const { list, ns } = dropdownConfig[item.dropdown];
                    const open = openMobileGroup === item.key;
                    return (
                      <li key={item.key}>
                        <button
                          type="button"
                          className="w-full flex items-center justify-between py-3 text-sm uppercase tracking-wider text-[var(--charcoal)]"
                          onClick={() => setOpenMobileGroup(open ? null : item.key)}
                          aria-expanded={open}
                          data-testid={`mobile-nav-${item.key}-trigger`}
                        >
                          {t(`nav.${item.key}`)}
                          <ChevronDown
                            className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
                          />
                        </button>
                        {open && (
                          <ul className="pl-4 pb-2 border-l border-[var(--border)] ml-1">
                            {list.map((li) => (
                              <li key={li.path}>
                                <Link
                                  to={li.path}
                                  className="block py-2 text-sm text-[var(--slate)] hover:text-[var(--navy)]"
                                  data-testid={`mobile-nav-${item.key}-item-${li.key}`}
                                >
                                  {t(`${ns}.${li.key}`)}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  }
                  return (
                    <li key={item.key}>
                      <NavLink
                        to={item.to}
                        end={item.to === "/"}
                        className="block py-3 text-sm uppercase tracking-wider text-[var(--charcoal)] hover:text-[var(--navy)]"
                        data-testid={`mobile-nav-${item.key}`}
                      >
                        {t(`nav.${item.key}`)}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </nav>
            <div className="p-6 border-t border-[var(--border)] space-y-3">
              <Link
                to="/request-sample"
                className="btn-primary w-full text-center text-sm"
                data-testid="cta-request-sample-mobile"
              >
                {t("nav.cta_sample")}
              </Link>
              <Link
                to="/contact"
                className="btn-outline w-full text-center text-sm"
                data-testid="cta-talk-mobile"
              >
                {t("nav.cta_talk")}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
